import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  FormHelperText,
  IconButton,
  Modal,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import Delete from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePermitTemplate,
  savePermitTemplate,
} from "../../../../store/actions/dataActions";

const LoadPermitTemplateModal = ({
  openModal,
  setOpenModal,
  selectedForms,
  setSelectedForms,
  city,
  setCity,
  permitType,
  setPermitType,
  county,
}) => {
  const { permitPackageTemplates } = useSelector((state) => state.nocData);
  const attachingNewPermit = useSelector(
    (state) => state.myPermitsComponent?.attachingNewPermit
  );

  const [templateError, setTemplateError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [isLoadMode, setIsLoadMode] = useState(true);
  const [isSaveModeDisabled, setIsSaveModeDisabled] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [loadedTemplateName, setLoadedTemplateName] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(false);

  const dispatch = useDispatch();

  const handleDeleteTemplate = async () => {
    try {
      setDeletingTemplate(true);

      const resp = await dispatch(
        deletePermitTemplate(selectedTemplate?.templateID)
      );

      if (!resp?.success) {
        setTemplateError(resp?.error);
      } else {
        setSelectedTemplate({});
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unknown Error";
      setTemplateError(message);
    } finally {
      setDeletingTemplate(false);
      setTimeout(() => {
        setTemplateError("");
      }, 5000);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setButtonLoading(true);

      if (isLoadMode) {
        // Load Template
        // Block loading a template that uses an already-used permit type in additional mode
        if (
          attachingNewPermit?.started &&
          Array.isArray(attachingNewPermit?.addedPermits) &&
          attachingNewPermit.addedPermits.includes(selectedTemplate?.permitType)
        ) {
          setTemplateError(
            "This permit type is already attached to the current package."
          );
          return;
        }

        setPermitType(selectedTemplate?.permitType || "");
        setCity(selectedTemplate?.city);
        setSelectedForms(selectedTemplate?.selectedForms);
        setNewTemplateName(selectedTemplate?.templateName);
        setLoadedTemplateName(selectedTemplate?.templateName);

        setOpenModal(false);
      }
      // Save Template
      else {
        const resp = await dispatch(
          savePermitTemplate({
            county,
            city,
            permitType,
            newTemplateName,
            selectedForms,
          })
        );

        if (!resp?.success) {
          setTemplateError(resp?.error);
        } else {
          setOpenModal(false);
          setSelectedTemplate(resp?.data?.data);
          setLoadedTemplateName(resp?.data?.data?.templateName);
        }
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unknown Error";
      setTemplateError(message);
    } finally {
      setButtonLoading(false);
      setTimeout(() => {
        setTemplateError("");
      }, 5000);
    }
  };

  useEffect(() => {
    if (selectedForms && selectedForms.length > 1) {
      // Save Mode and Load Mode
      setIsSaveModeDisabled(false);
    } else {
      // Load Mode Only
      if (!isSaveModeDisabled || !isLoadMode) {
        setIsSaveModeDisabled(true);
        if (!isLoadMode) {
          setIsLoadMode(true);
        }
      }
    }
  }, [selectedForms, isSaveModeDisabled, isLoadMode, openModal]);

  return (
    <Modal open={openModal}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: "15px",
          boxShadow: 24,
          p: 4,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Typography align="center" variant="h5" component="h2">
          Permit Package Form Selection Templates
        </Typography>
        <Typography textAlign="center" variant="body2" sx={{ mt: 1 }}>
          Save or load your current form selection as a template to quickly
          reuse it when creating future permit packages.
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <Typography>Save</Typography>
          <Switch
            disabled={isSaveModeDisabled || buttonLoading || deletingTemplate}
            checked={isLoadMode}
            onChange={(e) => {
              setIsLoadMode(e.target.checked);
              setNewTemplateName(loadedTemplateName);
            }}
          />
          <Typography>Load</Typography>
        </Box>
        <Box sx={{ minHeight: "96px", mt: 4 }}>
          {isLoadMode ? (
            <Autocomplete
              disabled={deletingTemplate}
              sx={{ width: "100%" }}
              options={permitPackageTemplates}
              value={selectedTemplate || null}
              onChange={(e, newValue) => {
                setSelectedTemplate(newValue);
              }}
              getOptionLabel={(option) => option.templateName || ""}
              getOptionDisabled={(option) =>
                attachingNewPermit?.started &&
                Array.isArray(attachingNewPermit?.addedPermits) &&
                attachingNewPermit.addedPermits.includes(option?.permitType)
              }
              renderOption={(props, option) => {
                const isDisabled =
                  attachingNewPermit?.started &&
                  Array.isArray(attachingNewPermit?.addedPermits) &&
                  attachingNewPermit.addedPermits.includes(option?.permitType);
                return (
                  <li
                    {...props}
                    style={{
                      opacity: isDisabled ? 0.5 : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        {option.templateName}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {option?.permitType || ""}
                        {option?.city ? ` — ${option.city}` : ""}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Template" />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 200, // <- limit height
                    overflowY: "auto", // <- enable scroll
                  },
                },
              }}
            />
          ) : (
            <TextField
              sx={{ mb: 2 }}
              fullWidth
              variant="filled"
              label="Template Name"
              name="newTemplateName"
              value={newTemplateName}
              onChange={(e) => {
                setNewTemplateName(e.target.value);
              }}
            />
          )}
        </Box>

        <Box textAlign="center" mb={1}>
          <FormHelperText
            sx={{
              color: "error.main",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {templateError}
          </FormHelperText>
        </Box>
        <Box sx={{ display: "flex" }} gap={1}>
          <LoadingButton
            fullWidth
            disabled={
              isLoadMode
                ? !Boolean(selectedTemplate) ||
                  Object.keys(selectedTemplate).length === 0 ||
                  deletingTemplate
                : newTemplateName === ""
            }
            loading={buttonLoading}
            onClick={handleSaveTemplate}
            variant="contained"
            color="primary"
          >
            {isLoadMode ? "Load Template" : "Save Template"}
          </LoadingButton>

          {isLoadMode && (
            <Tooltip
              title={
                !Boolean(selectedTemplate) ||
                Object.keys(selectedTemplate).length === 0
                  ? ""
                  : "Remove Template"
              }
            >
              <span>
                <IconButton
                  disabled={
                    !Boolean(selectedTemplate) ||
                    Object.keys(selectedTemplate).length === 0
                  }
                  onClick={handleDeleteTemplate}
                  color="error"
                  sx={{ height: 40, width: 40 }}
                  className={deletingTemplate ? "rotate" : undefined}
                >
                  {deletingTemplate ? <RefreshIcon /> : <Delete />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <Button
          disabled={buttonLoading || deletingTemplate}
          onClick={() => {
            setOpenModal(false);
          }}
          color="error"
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
          fullWidth
        >
          Close
        </Button>
      </Box>
    </Modal>
  );
};

export default LoadPermitTemplateModal;
